import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { formatUsd } from "@/lib/marketplace/queries";
import { listMyMarketplaceOrders } from "@/lib/marketplace/orders.functions";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/orders")({
  head: () => ({
    meta: [
      { title: "My Marketplace orders | BeHot.Now" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OrdersPage,
});

const STATUS_TONE: Record<string, string> = {
  completed: "bg-emerald-500/10 text-emerald-600",
  cancelled: "bg-muted text-muted-foreground",
  delivered: "bg-amber-500/10 text-amber-600",
};

function OrdersPage() {
  const { t } = useT();
  const { user, loading } = useAuth();
  const [role, setRole] = useState<"buyer" | "seller">("buyer");
  const { data, isLoading } = useQuery({
    queryKey: ["mp-orders", role, user?.id ?? null],
    enabled: Boolean(user),
    queryFn: () => listMyMarketplaceOrders({ data: { role } }),
  });

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="font-display text-2xl font-bold">{t("mp.orders.title")}</h1>
        <div className="mt-4 flex gap-2">
          <Button variant={role === "buyer" ? "hot" : "outline"} size="sm" onClick={() => setRole("buyer")}>
            {t("mp.orders.buying")}
          </Button>
          <Button variant={role === "seller" ? "hot" : "outline"} size="sm" onClick={() => setRole("seller")}>
            {t("mp.orders.selling")}
          </Button>
        </div>

        {!user && !loading && (
          <p className="mt-8 text-sm text-muted-foreground">
            <Link to="/login" className="underline">
              {t("mp.common.sign_in")}
            </Link>{" "}
            {t("mp.orders.sign_in_suffix")}
          </p>
        )}

        {isLoading && <Skeleton className="mt-6 h-24 w-full" />}

        {data && data.length === 0 && (
          <p className="mt-8 text-sm text-muted-foreground">{t("mp.orders.empty")}</p>
        )}

        <ul className="mt-6 space-y-3">
          {(data ?? []).map((o) => (
            <li key={o.id}>
              <Link
                to="/marketplace/order/$orderId"
                params={{ orderId: o.id }}
                className="card-surface flex items-center gap-4 p-4 transition-shadow hover:shadow-md"
              >
                {o.service?.cover_url && (
                  <img src={o.service.cover_url} alt="" className="size-14 rounded-lg object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{o.service?.title ?? t("mp.orders.custom_order")}</p>
                  <p className="text-xs text-muted-foreground">
                    {role === "buyer" ? t("mp.common.seller") : t("mp.common.buyer")}:{" "}
                    {o.counterparty?.display_name || o.counterparty?.handle || "—"} ·{" "}
                    {new Date(o.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold">{formatUsd(o.total_cents)}</p>
                  <Badge className={STATUS_TONE[o.status] ?? "bg-primary/10 text-primary"} variant="secondary">
                    {o.status.replace(/_/g, " ")}
                  </Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </MarketplaceGate>
  );
}
